import {
  Button,
  Divider,
  makeStyles,
  Paper,
  Typography,
  Box,
} from "@material-ui/core";
import { AccountCircle, CloudUpload } from "@material-ui/icons";
import React, { useEffect, useRef, useState } from "react";
import { useFirebase } from "../../Firebase/FirebaseContext";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import CustomButton from "../Buttons/CustomButton";
import { useLocation } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
  getAuth,
  updateProfile,
  updatePassword as updatePasswordFn,
} from "firebase/auth";
import { useSnackbar } from "notistack";
import { set } from "date-fns";

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: 800,
    margin: "0 auto",
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(4),
  },
  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  avatar: {
    fontSize: 48,
    color: theme.palette.primary.main,
  },
  card: {
    padding: theme.spacing(4),
    borderRadius: 20,
    marginBottom: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
  },
  imageContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  profileImage: {
    width: 200,
    height: 200,
    borderRadius: "50%",
    objectFit: "cover",
    border: `4px solid ${theme.palette.primary.main}`,
    boxShadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  },
  uploadButton: {
    marginTop: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  infoSection: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
    marginBottom: theme.spacing(2),
  },
  infoLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  infoValue: {
    fontSize: "1rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  passwordSection: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
    marginTop: theme.spacing(3),
  },
  passwordInput: {
    width: "100%",
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    fontSize: "1rem",
    fontFamily: "inherit",
    marginBottom: theme.spacing(2),
    "&:focus": {
      outline: "none",
      borderColor: theme.palette.primary.main,
    },
  },
  requirements: {
    listStyle: "none",
    padding: 0,
    margin: theme.spacing(2, 0),
    "& li": {
      padding: theme.spacing(0.5, 0),
      color: theme.palette.text.secondary,
      fontSize: "0.875rem",
      "&:before": {
        content: '"•"',
        color: theme.palette.primary.main,
        fontWeight: "bold",
        display: "inline-block",
        width: "1em",
        marginRight: theme.spacing(1),
      },
    },
  },
  buttonGroup: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: "wrap",
  },
}));

export default function Profile() {
  const { firebase } = useFirebase();
  const classes = useStyles();
  const location = useLocation();
  const userEmail = location.state.user;
  const { enqueueSnackbar } = useSnackbar();
  const [currentUser, setCurrentUser] = useState();
  const [userImage, setUserImage] = useState();
  const [file, setFile] = useState();
  const [refresh, setRefresh] = useState(false);
  const selectFile = useRef(null);
  const [updatePassword, setUpdatePassword] = useState(false);
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (userEmail) {
      getDoc(doc(firebase.db, "users", userEmail)).then((docData) => {
        setCurrentUser(docData.data());
        const imgRef = ref(getStorage(), docData.data().profile_picture);
        getDownloadURL(ref(getStorage(), imgRef))
          .then((imgUrl) => setUserImage(imgUrl))
          .catch((e) => console.log(e));
      });
    }
  }, [refresh]);

  const fileSelectionClick = () => {
    selectFile.current.click();
  };

  const updateFirebasePassword = () => {
    const auth = getAuth();
    console.log(auth.currentUser);
    updatePasswordFn(auth.currentUser, password)
      .then(() => {
        enqueueSnackbar("Mot de passe modifié avec succès", {
          variant: "success",
        });
        setUpdatePassword(false);
      })
      .catch((e) => {
        enqueueSnackbar("Erreur", {
          variant: "error",
        });
      });
  };

  const submit = () => {
    const storage = getStorage();
    const trimedName = file.name.replace(/\s+/g, "");
    const url = `users/${userEmail}/${trimedName}`;
    const profileImgRef = ref(storage, url);
    uploadBytes(profileImgRef, file).then(() => {
      const auth = getAuth();
      updateProfile(auth.currentUser, {
        photoURL: url,
      })
        .then(() => {
          const userRef = doc(firebase.db, "users", userEmail);
          getDoc(userRef).then((docData) => {
            if (userImage) {
              const pictureRef = ref(storage, docData.data().profile_picture);
              deleteObject(pictureRef);
            }
            updateDoc(userRef, {
              profile_picture: url,
            });
            enqueueSnackbar("Votre image de profil à bien été changée", {
              variant: "success",
            });
            setRefresh(!refresh);
          });
        })
        .catch((e) => {
          enqueueSnackbar("Erreur", {
            variant: "error",
          });
        });
    });
  };

  return (
    <div className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.title}>
          <AccountCircle className={classes.avatar} />
          {currentUser
            ? `${currentUser.name} ${currentUser.lastName}`
            : "Profil utilisateur"}
        </Typography>
      </Box>

      <Paper className={classes.card} elevation={0}>
        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>
            Photo de profil
          </Typography>
          <Box className={classes.imageContainer}>
            {userImage && (
              <img
                src={userImage}
                className={classes.profileImage}
                alt="Profile"
              />
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={fileSelectionClick}
              startIcon={!file && <CloudUpload />}
            >
              {file ? file.name : "Choisir une image"}
              <input
                type="file"
                hidden
                accept=".png,.jpg"
                ref={selectFile}
                onChange={(e) => {
                  setFile(e.target.files[0]);
                }}
                onClick={(e) => {
                  e.target.value = null;
                }}
              />
            </Button>
            {file && <CustomButton title="Confirmer" onClick={submit} />}
          </Box>
        </Box>

        <Divider className={classes.divider} />

        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>
            Informations du compte
          </Typography>
          {currentUser && (
            <Box className={classes.infoSection}>
              <Typography className={classes.infoLabel}>Email</Typography>
              <Typography className={classes.infoValue}>{userEmail}</Typography>
            </Box>
          )}
        </Box>

        <Divider className={classes.divider} />

        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>
            Sécurité
          </Typography>
          {!updatePassword && (
            <CustomButton
              title="Modifier le mot de passe"
              onClick={() => {
                setUpdatePassword(!updatePassword);
              }}
            />
          )}

          {updatePassword && (
            <Box className={classes.passwordSection}>
              <Typography
                variant="h6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Modifier le mot de passe
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginBottom: 16 }}
              >
                Veuillez entrer votre nouveau mot de passe respectant les
                critères de sécurité suivants :
              </Typography>
              <ul className={classes.requirements}>
                <li>8 caractères minimum</li>
                <li>1 majuscule</li>
                <li>1 chiffre</li>
                <li>1 caractère spécial</li>
              </ul>
              <input
                type="password"
                placeholder="Nouveau mot de passe"
                onChange={(e) => setPassword(e.target.value)}
                className={classes.passwordInput}
              />
              <Box className={classes.buttonGroup}>
                <CustomButton
                  title="Confirmer"
                  onClick={updateFirebasePassword}
                />
                <Button
                  variant="outlined"
                  onClick={() => setUpdatePassword(false)}
                >
                  Annuler
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </div>
  );
}
